import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import { Tabs } from 'app/shared-components/structure';
import useEventTracker from 'app/hooks/useEventTracker';
import { SERVICES_EVENTS } from 'app/tracking';
import { useUserRoles } from 'app/hooks';
import { DialogModal } from 'app/shared-components/modals';

const tabs = [
  {
    label: 'Listed services',
    value: 'listed-services',
    path: '/services',
  },
  {
    label: 'Consultation rooms',
    value: 'consultation-rooms',
    path: '/services/consultation-rooms',
  },
  {
    label: 'Block-out days',
    value: 'block-out-days',
    path: '/services/block-out-days',
  },
  {
    label: 'Categories',
    value: 'categories',
    path: '/services/service-templates/categories',
  },
  {
    label: 'Group services',
    value: 'group-services',
    path: '/services/service-templates',
  },
  {
    label: 'Group questionnaires',
    value: 'group-questionnaires',
    path: '/services/group-questionnaires',
  },
];

const pharmacistRestrictedTabs = ['categories', 'group-services'];

export default function GroupServicesLayout({ children }) {
  const trackEvent = useEventTracker();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedPharmacy } = useSelector((state) => state.pharmacySelector);
  const { isPharmacyAdmin, isSupport } = useUserRoles();
  const [pharmacistBlockModalOpened, setPharmacistBlockModalOpened] = useState(false);

  const selectedTab = useMemo(() => {
    // Sort tabs by path length (longest first) to match most specific paths first
    const sortedTabs = [...tabs].sort((a, b) => b.path.length - a.path.length);
    return sortedTabs.find((tab) => location.pathname.includes(tab.path))?.value;
  }, [location.pathname]);

  function handleTabChange(event, newValue) {
    if (!isPharmacyAdmin && !isSupport && pharmacistRestrictedTabs.includes(newValue)) {
      setPharmacistBlockModalOpened(true);
      return;
    }

    // Track section clicked using lookup map
    const sectionEvent = tabEvents[newValue];
    if (sectionEvent) trackEvent(sectionEvent.action, sectionEvent);

    const newPath = tabs.find((tab) => tab.value === newValue)?.path;
    if (newPath) {
      let newUrl = newPath;
      const currentParams = new URLSearchParams(searchParams);

      if (
        newPath.startsWith('/services/service-templates') ||
        newPath.startsWith('/services/group-questionnaires')
      ) {
        const organisationId = currentParams.get('organisationId') || selectedPharmacy?.chain_id;
        if (organisationId) {
          newUrl += `?organisationId=${organisationId}`;
        }
      }

      navigate(newUrl, { replace: true });
    }
  }

  const isTabRestricted =
    !isPharmacyAdmin && !isSupport && pharmacistRestrictedTabs.includes(selectedTab);

  useEffect(() => {
    if (isTabRestricted) navigate(tabs[0].path, { replace: true });
  }, [isTabRestricted, navigate]);
  if (isTabRestricted) return null;

  return (
    <Box className="grow flex flex-col px-20 py-16 gap-16 bg-white overflow-hidden">
      <Box className="flex items-center">
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab}
          handleChange={handleTabChange}
          dividerAfterIndex={2}
        />
      </Box>
      {children}
      <DialogModal
        open={pharmacistBlockModalOpened}
        handleClose={() => setPharmacistBlockModalOpened(false)}
        title="Access Restricted"
        content="You do not have permission to access this resource. Only users with the Pharmacy Admin role can view this page. If you believe you should have access, please contact Healthera support."
        actions={[
          {
            title: 'Dismiss',
            type: 'transparent',
            cb: () => setPharmacistBlockModalOpened(false),
            outlined: true,
          },
        ]}
      />
    </Box>
  );
}

const tabEvents = {
  'listed-services': SERVICES_EVENTS.LISTED_SERVICES.SECTION_CLICKED,
  'consultation-rooms': SERVICES_EVENTS.CONSULTATION_ROOMS.SECTION_CLICKED,
  categories: SERVICES_EVENTS.CATEGORIES.SECTION_CLICKED,
  'group-services': SERVICES_EVENTS.GROUP_SERVICES.SECTION_CLICKED,
  'block-out-days': SERVICES_EVENTS.BLOCK_OUT_DAYS.SECTION_CLICKED,
};
